import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import NewsletterSignup from "../components/NewsletterSignup";
import { Search, Calendar, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { NEWS } from "../data/mock";

const BlogPage = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const categories = ["All", ...Array.from(new Set(NEWS.map((n) => n.category)))];
  const filtered = NEWS.filter(
    (n) =>
      (category === "All" || n.category === category) &&
      (query === "" ||
        n.title.toLowerCase().includes(query.toLowerCase()) ||
        n.excerpt.toLowerCase().includes(query.toLowerCase()))
  );
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Stories & Insights"
          title="From the Recovery Connections journal."
          description="Articles, stories and resources from our team and the recovery community we serve."
          breadcrumbs={[{ label: "Blog" }]}
        />

        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            {/* Filter row */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-10">
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-4 h-9 rounded-full text-[13px] font-medium border transition-all ${
                      category === c
                        ? "bg-[hsl(var(--rc-deep))] text-white border-[hsl(var(--rc-deep))]"
                        : "bg-white text-[hsl(var(--rc-charcoal))] border-[hsl(var(--border))] hover:border-[hsl(var(--rc-purple))]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="relative max-w-md w-full md:w-auto">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <Input
                  placeholder="Search articles…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-11 pl-10 bg-white border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--rc-purple))]"
                />
              </div>
            </div>

            {/* Featured */}
            {featured && (
              <article className="grid md:grid-cols-12 gap-8 md:gap-12 items-center mb-16">
                <div className="md:col-span-7">
                  <div className="relative aspect-[16/10] rounded-2xl overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className="md:col-span-5">
                  <span className="inline-block px-3 py-1 bg-[hsl(var(--rc-purple-soft))] text-[hsl(var(--rc-purple))] text-[11px] tracking-[0.18em] uppercase font-semibold rounded-full">
                    Featured · {featured.category}
                  </span>
                  <h2 className="font-serif-display mt-4 text-[28px] md:text-[36px] leading-[1.15] font-semibold text-[hsl(var(--rc-deep))] hover:text-[hsl(var(--rc-purple))] transition-colors">
                    <a href="#">{featured.title}</a>
                  </h2>
                  <p className="mt-4 text-[15px] leading-[1.7] text-[hsl(var(--rc-slate))]">
                    {featured.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-[12.5px] text-[hsl(var(--muted-foreground))]">
                    <Calendar className="w-3.5 h-3.5" />
                    {featured.date}
                  </div>
                  <a
                    href="#"
                    className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-[hsl(var(--rc-purple))] hover:text-[hsl(var(--rc-deep))] transition-colors group"
                  >
                    Read article
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </article>
            )}

            {/* Rest */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((n) => (
                <article
                  key={n.title}
                  className="rc-card-hover bg-white border border-[hsl(var(--border))] rounded-2xl overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={n.image}
                      alt={n.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--rc-purple))] font-semibold rounded-full">
                      {n.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--muted-foreground))]">
                      <Calendar className="w-3.5 h-3.5" />
                      {n.date}
                    </div>
                    <h3 className="font-serif-display mt-3 text-[20px] leading-[1.3] font-semibold text-[hsl(var(--rc-deep))] hover:text-[hsl(var(--rc-purple))] transition-colors">
                      <a href="#">{n.title}</a>
                    </h3>
                    <p className="mt-3 text-[14px] leading-[1.65] text-[hsl(var(--rc-slate))] flex-1">
                      {n.excerpt}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20 text-[hsl(var(--muted-foreground))]">
                No articles found. Try a different search.
              </div>
            )}
          </div>
        </section>

        <section className="py-20 rc-section-soft">
          <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
            <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
              Stay in the loop
            </p>
            <h3 className="font-serif-display mt-3 text-[30px] md:text-[36px] font-semibold text-[hsl(var(--rc-deep))]">
              Get new stories in your inbox.
            </h3>
            <p className="mt-3 text-[15px] text-[hsl(var(--rc-slate))]">
              Once-a-month updates from the Recovery Connections community.
            </p>
            <div className="mt-8 max-w-lg mx-auto">
              <NewsletterSignup />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
